import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { inviteConfirm } from '../api/client';
import logo from '../logo.svg';

const MIN_PASSWORD_LENGTH = 8;

function classifyInviteTokenError(error) {
  const candidates = [
    error?.code,
    error?.detail?.code,
    error?.type,
    error?.detail,
    error?.message,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());

  for (const value of candidates) {
    if (value === 'already_accepted' || value.includes('already accepted')) {
      return 'ALREADY_ACCEPTED';
    }
    if (value === 'already_used' || value === 'used' || value.includes('already used')) {
      return 'USED';
    }
    if (value === 'expired' || value === 'expired_token' || value.includes('expired')) {
      return 'EXPIRED';
    }
    if (value === 'invalid' || value === 'invalid_token' || value === 'invalid_or_expired_token' || value.includes('invalid')) {
      return 'INVALID';
    }
  }

  return null;
}

function AcceptInvitePage() {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const hasToken = Boolean(token);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!hasToken) {
      setErrorMessage('Invite link is missing or invalid. Ask an admin to send you a new invite.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await inviteConfirm(token, newPassword, marketingConsent);
      setIsSuccess(true);
    } catch (error) {
      const inviteTokenErrorType = classifyInviteTokenError(error);
      if (inviteTokenErrorType === 'INVALID') {
        setErrorMessage('This invite link is invalid.');
      } else if (inviteTokenErrorType === 'EXPIRED') {
        setErrorMessage('This invite link has expired. Please ask an administrator to send a new invite.');
      } else if (inviteTokenErrorType === 'USED') {
        setErrorMessage('This invite link has already been used.');
      } else if (inviteTokenErrorType === 'ALREADY_ACCEPTED') {
        setErrorMessage('This invite has already been accepted. Please sign in to your account.');
      } else {
        const rawMessage = String(error?.message || '').trim();
        setErrorMessage(rawMessage || 'Could not accept invite. The link may have expired.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page page-shell auth-page">
      <div className="public-shell">
        <section className="card public-panel">
          <div className="public-panel-copy">
            <div className="public-brand">
              <img src={logo} alt="Sumrise Logo" />
              <span>Sumrise</span>
            </div>
            <p className="public-kicker">Invite activation</p>
            <h1>Accept Invite</h1>
            <p>Set a password to activate your account and complete the final step of invite acceptance.</p>
          </div>
          <ul className="public-feature-list">
            <li>
              <strong>Single-use links</strong>
              <p>Invite links are time-limited and can only be accepted once.</p>
            </li>
            <li>
              <strong>Email verification included</strong>
              <p>Invite acceptance completes the account verification step for beta onboarding.</p>
            </li>
          </ul>
        </section>

        <section className="card auth-card">
          <div className="auth-card-header">
            <p className="public-kicker">Create password</p>
            <p className="muted-text">Choose a password with at least {MIN_PASSWORD_LENGTH} characters.</p>
          </div>
          {!hasToken ? <p role="status" className="notice">Invite link is missing or invalid. Ask an admin to send you a new invite.</p> : null}
          {isSuccess ? (
            <div className="stack-sm">
              <p role="status" className="notice notice-info">Invite accepted. Please log in.</p>
              <Link to="/login?notice=invite_accepted" className="btn btn-primary auth-action">Go to Login</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="stack-md">
              <div>
                <label htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              <div>
                <label htmlFor="confirm-new-password">Confirm New Password</label>
                <input
                  id="confirm-new-password"
                  name="confirm-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              <fieldset>
                <legend>Permission to quote feedback (optional)</legend>
                <p className="muted-text">
                  May Sumrise publicly quote feedback written about the app &mdash; your own, or
                  that of any child linked to your account? Quotes never name anyone. Leaving it
                  unanswered means no. You can change or withdraw permission at any time from
                  your Account page. If you are under 18, your parent or guardian decides this and
                  any answer you give here is not recorded.
                </p>
                <label>
                  <input
                    type="radio"
                    name="marketing-consent"
                    checked={marketingConsent === true}
                    onChange={() => setMarketingConsent(true)}
                  />{' '}
                  Yes, I give permission
                </label>
                <label>
                  <input
                    type="radio"
                    name="marketing-consent"
                    checked={marketingConsent === false}
                    onChange={() => setMarketingConsent(false)}
                  />{' '}
                  No, I do not give permission
                </label>
              </fieldset>
              <button type="submit" className="btn btn-primary auth-action" disabled={isSubmitting || !hasToken}>
                {isSubmitting ? 'Accepting...' : 'Accept Invite'}
              </button>
            </form>
          )}
          {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
          <div className="auth-card-footer">
            <p>
              <Link to="/login">Back to Login</Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default AcceptInvitePage;
